import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Avatar, Checkbox, Flex, Form, InputNumber, Radio, Select } from 'antd';
import classnames from 'classnames';
import { collect } from 'collect.js';
import { Stringable } from '../../../services/helpers/Stringable';
import YesNoRadio from '../../../components/form/general/YesNoRadio';
import useHelpers from '../../../hooks/context/useHelpers';
import ManagerSelect from '../../../components/form/business/ManagerSelect';
import { ManagerRole } from '../../../hooks/auth/usePermissions';

export const OtherPlatforms = {
  Lieferando: 'lieferando',
  Wolt: 'wolt',
  UberEats: 'uber_eats',
};

export const PosSystems = {
  Liefersoft: 'liefersoft',
  Winorder: 'win_order',
  Sides: 'sides',
  Orderbird: 'order_bird',
  Verctron: 'verctron',
  Ordersmart: 'order_smart',
  Expertorder: 'expert_order',
  Lightspeed: 'light_speed',
  Allo: 'allo',
  Other: 'other',
};

export const LeadSources = {
  SDR: 'sdr',
  Inbound: 'inbound',
  Referral: 'referral',
  SelfGeneratedCold: 'self_generated_cold',
  SelfGeneratedReferral: 'self_generated_referral',
  Tipster: 'tipster',
};

function OtherPlatformItem({ platform, checked, togglePlatform }) {
  const id = `platform-${platform}-checkbox`;
  const avatarColor = {
    [OtherPlatforms.Lieferando]: '#FF8000',
    [OtherPlatforms.Wolt]: '#009DE0',
    [OtherPlatforms.UberEats]: '#06C167',
  }[platform];

  return (
    <label
      htmlFor={id}
      className={classnames(
        'flex justify-between p-sm rounded-lg shadow border border-solid border-gray-300 hover:border-brand-primary ease-in-out transition-colors duration-150 cursor-pointer',
        {
          '!border-brand-primary bg-brand-primary/10': checked,
        },
      )}
    >
      <Flex gap="middle">
        <Avatar style={{ backgroundColor: avatarColor }}>{platform[0].toUpperCase()}</Avatar>
        <h6 className="my-auto">{Stringable.toHuman(platform)}</h6>
      </Flex>
      <Checkbox checked={checked} onChange={() => togglePlatform(platform)} id={id} className="scale-125" />
    </label>
  );
}

function LeadSourceItem({ source, checked, onChange }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Company:AddCompanyModal' });

  const id = `lead-source-${source}-radio`;

  return (
    <label
      htmlFor={id}
      className={classnames(
        'flex gap-md p-sm rounded-lg shadow border border-solid border-gray-300 hover:border-brand-primary ease-in-out transition-colors duration-150 cursor-pointer',
        {
          '!border-brand-primary bg-brand-primary/10': checked,
        },
      )}
    >
      <Radio checked={checked} onChange={() => onChange(source)} id={id} className="scale-125" />
      <div>
        <h6 className="my-auto">{t(`form.lead_source.options.${source}.title`)}</h6>
        <p className="my-auto">{t(`form.lead_source.options.${source}.description`)}</p>
      </div>
    </label>
  );
}

export default function BusinessDetails({ data, onChange }) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Modals:Company:AddCompanyModal' });

  const { getCurrencySymbol } = useHelpers();

  const isPlatformChecked = useCallback(
    (platform) => data.company_info.other_platforms.includes(platform),
    [data.company_info.other_platforms],
  );
  const togglePlatform = (platform) =>
    onChange(
      'company_info.other_platforms',
      collect(data.company_info.other_platforms)
        .when(
          isPlatformChecked(platform),
          (collection) => collection.reject((item) => item === platform),
          (collection) => collection.push(platform),
        )
        .toArray(),
    );

  const posSystemOptions = useMemo(
    () =>
      Object.values(PosSystems).map((item) => ({
        label: t(`form.pos_system.options.${item}`, { defaultValue: Stringable.toHuman(item) }),
        value: item,
      })),
    [i18n.language],
  );

  useEffect(() => {
    if (data.company_info?.lead_source === LeadSources.SDR) {
      return;
    }

    if (!data.company_info?.sdr_id) {
      return;
    }

    onChange('company_info.sdr', null);
  }, [data.company_info?.lead_source, data.company_info?.sdr_id]);

  return (
    <div>
      <h3 className="mb-xs">{t('business_details.title')}</h3>
      <p className="mb-md text-gray-600">{t('business_details.description')}</p>
      <Form layout="vertical" autoComplete="off">
        <Form.Item label={<span className="text-md font-medium mb-none">{t('form.other_platforms.label')}</span>}>
          <p className="text-gray-600 text-sm mb-xs">{t('form.other_platforms.description')}</p>
          <div className="space-y-sm">
            {Object.values(OtherPlatforms).map((item) => (
              <OtherPlatformItem
                platform={item}
                checked={isPlatformChecked(item)}
                togglePlatform={togglePlatform}
                key={item}
              />
            ))}
          </div>
        </Form.Item>
        <Form.Item label={t('form.pos_system.label')}>
          <Select
            value={data.company_info.pos_system}
            options={posSystemOptions}
            onChange={(value) => onChange('company_info.pos_system', value)}
            placeholder={t('form.pos_system.placeholder')}
          />
        </Form.Item>
        <Form.Item>
          <InputNumber
            prefix={getCurrencySymbol()}
            value={data.company_info.monthly_sales_other_platforms?.[0]}
            onChange={(val) => onChange('company_info.monthly_sales_other_platforms', [val])}
            placeholder={t('form.monthly_sales_other_platforms.other.placeholder')}
            controls={false}
            className="w-full"
          />
        </Form.Item>
        <Form.Item label={<span className="text-md font-medium mb-none">{t('form.lead_source.label')}</span>}>
          <p className="text-gray-600 text-sm mb-xs">{t('form.lead_source.description')}</p>
          <div className="space-y-sm">
            {Object.values(LeadSources).map((item) => (
              <LeadSourceItem
                source={item}
                checked={data.company_info.lead_source === item}
                onChange={(value) => onChange('company_info.lead_source', value)}
                key={item}
              />
            ))}
          </div>
        </Form.Item>
        <Form.Item
          preserve={false}
          hidden={data.company_info.lead_source !== LeadSources.SDR}
          label={<span className="text-md font-medium mb-none">SDR</span>}
        >
          <p className="text-gray-600 text-sm mb-xs">{t('form.sdr.description')}</p>
          <ManagerSelect
            value={data.company_info.sdr_id}
            onChange={(value) => onChange('company_info.sdr_id', value)}
            roles={[ManagerRole.SDR]}
            status
          />
        </Form.Item>
        <Form.Item label={<span className="text-md font-medium mb-none">{t('form.has_instagram_profile.label')}</span>}>
          <p className="text-gray-600 text-sm mb-xs">{t('form.has_instagram_profile.description')}</p>
          <YesNoRadio
            field="has_instagram_profile"
            value={data.company_info.has_instagram_profile}
            onChange={(value) => onChange('company_info.has_instagram_profile', value)}
          />
        </Form.Item>
      </Form>
    </div>
  );
}
